import { FC, useState, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { UserContext } from '@contexts/UserProvider';
import { replaceGithubPat, disconnectGithub } from '@config/getGithubApis';
import styles from './GithubEditPatModal.module.scss';

interface GithubEditPatModalProps {
    visible: boolean;
    onCancel: () => void;
}

const GithubEditPatModal: FC<GithubEditPatModalProps> = ({ visible, onCancel }) => {
    const [pat, setPat] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { refreshGithubCredentialStatus, setGithubConnected, setGithubLogin, setGithubLastAuthFailureAt } =
        useContext(UserContext);

    const handleSubmit = async () => {
        if (!pat.trim()) return;

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await replaceGithubPat(pat.trim());
            if (response && response.code === 1) {
                await refreshGithubCredentialStatus();
                setPat('');
                onCancel();
            } else {
                setErrorMessage(response?.description || 'Failed to update GitHub token.');
            }
        } catch (error: any) {
            const msg = error?.response?.data?.description || 'Failed to update GitHub token.';
            setErrorMessage(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (
            !window.confirm('Disconnect GitHub? Your session links will remain but pushes will fail until reconnected.')
        ) {
            return;
        }

        setIsDisconnecting(true);
        try {
            await disconnectGithub();
            setGithubConnected(false);
            setGithubLogin('');
            setGithubLastAuthFailureAt(null);
            setPat('');
            onCancel();
        } catch (error) {
            console.error('Error disconnecting GitHub', error);
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handleCancel = () => {
        setPat('');
        setErrorMessage('');
        onCancel();
    };

    return (
        <Modal
            visible={visible}
            title="Update GitHub token"
            ariaLabel="Update GitHub token"
            onCancel={handleCancel}
            modalStyle={{ width: '480px' }}
            footer={
                <>
                    <Button kind="secondary" onClick={handleCancel} disabled={isLoading || isDisconnecting}>
                        Cancel
                    </Button>
                    <Button
                        kind="primary"
                        onClick={handleSubmit}
                        disabled={!pat.trim() || isLoading || isDisconnecting}>
                        {isLoading ? 'Updating...' : 'Update'}
                    </Button>
                </>
            }>
            <div className={styles['edit-pat-modal-body']}>
                <Input
                    type="password"
                    title="New personal access token"
                    value={pat}
                    onChange={(v: string) => {
                        setPat(v);
                        setErrorMessage('');
                    }}
                    hideTitleOnInput={false}
                />
                <p className={styles['helper-text']}>
                    Token must have <code>repo</code> scope.
                </p>
                {errorMessage && (
                    <p className={styles['error-message']}>
                        <span className="Appkit4-icon icon-alert-outline" aria-hidden="true" />
                        {errorMessage}
                    </p>
                )}
                <div className={styles['disconnect-row']}>
                    <button
                        className={styles['disconnect-btn']}
                        onClick={handleDisconnect}
                        disabled={isDisconnecting || isLoading}>
                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect GitHub'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default GithubEditPatModal;
